import React, { useState } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { referencesApi } from '../../../api/references';
import useEscapeKey from '../../../hooks/useEscapeKey';

interface AddReferenceModalProps {
  candidateId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AddReferenceModal = ({ candidateId, isOpen, onClose, onSuccess }: AddReferenceModalProps) => {
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Close modal when Escape key is pressed
  useEscapeKey(onClose);

  // Form state
  const [formData, setFormData] = useState({
    full_name: '',
    company: '',
    relationship_type: 'leader',
    department: 'human_resources',
    custom_department: '',
    email: '',
    phone: '',
    linkedin: '',
    years_worked: 1,
    seniority: 'manager',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'years_worked' ? parseInt(value, 10) || 0 : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Create a copy of the form data for submission
      const submissionData = { ...formData };
      
      // If department is 'other', use the custom department value
      if (submissionData.department === 'other') {
        if (submissionData.custom_department) {
          console.log(`Using custom department: "${submissionData.custom_department}"`);
          submissionData.department = submissionData.custom_department;
        } else {
          console.log('Warning: "other" department selected but no custom department provided');
        }
      }
      
      // Remove custom_department as it's not part of the API model
      delete submissionData.custom_department;
      
      // Verify candidateId exists
      if (!candidateId) {
        console.error('Missing candidate_id:', candidateId);
        throw new Error('Missing candidate ID');
      }
      
      // Add candidate_id to the reference data
      const referenceData = {
        ...submissionData,
        candidate_id: candidateId
      };
      
      console.log('Submitting reference data:', JSON.stringify(referenceData));
      try {
        await referencesApi.create(referenceData);
        console.log('Reference created successfully');
      } catch (error) {
        console.error('Error from API:', error);
        throw error;
      }
      
      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
      
      // Close the modal
      onClose();
    } catch (error) {
      console.error('Failed to add reference:', error);
      // Log more details about the error
      if (error instanceof Error) {
        console.error('Error details:', error.message, error.stack);
      } else if (typeof error === 'object' && error !== null) {
        console.error('Error object:', JSON.stringify(error));
      }
      
      // Use a more detailed error message if available
      if (typeof error === 'object' && error !== null && 'detail' in error) {
        const errorDetail = (error as any).detail;
        setError(typeof errorDetail === 'string' ? errorDetail : t('candidates.addReferenceModal.error'));
      } else {
        setError(t('candidates.addReferenceModal.error'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 z-[99999]" 
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      onClick={(e) => {
        // Close when clicking outside the modal
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div 
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl bg-white rounded-xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden"
        style={{ 
          maxHeight: '90vh',
          maxWidth: 'min(90vw, 56rem)',
          margin: '0 auto'
        }}
      >
        <div className="flex items-center justify-between p-5 border-b bg-blue-50/50 sticky top-0 z-10">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <span className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </span>
            {t('candidates.addReferenceModal.title')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="overflow-y-auto overflow-x-hidden flex-1">
          <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}
          
          <div className="mb-6 bg-blue-50/40 rounded-lg p-4 border border-blue-100">
            <h3 className="text-sm font-medium text-blue-600 mb-2">
              {t('candidates.addReferenceModal.personalInfo')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('candidates.addReferenceModal.fields.fullName')}*
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('candidates.addReferenceModal.fields.email')}*
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('candidates.addReferenceModal.fields.phone')}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('candidates.addReferenceModal.fields.linkedin')}
                </label>
                <input
                  type="url"
                  name="linkedin"
                  value={formData.linkedin}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://linkedin.com/in/username"
                />
              </div>
            </div>
          </div>

          <div className="mb-6 bg-blue-50/40 rounded-lg p-4 border border-blue-100">
            <h3 className="text-sm font-medium text-blue-600 mb-2">
              {t('candidates.addReferenceModal.professionalInfo')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('candidates.addReferenceModal.fields.company')}*
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('candidates.addReferenceModal.fields.department')}*
                </label>
                <select
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="human_resources">{t('departments.human_resources')}</option>
                  <option value="engineering">{t('departments.engineering')}</option>
                  <option value="product">{t('departments.product')}</option>
                  <option value="design">{t('departments.design')}</option>
                  <option value="marketing">{t('departments.marketing')}</option>
                  <option value="sales">{t('departments.sales')}</option>
                  <option value="support">{t('departments.support')}</option>
                  <option value="finance">{t('departments.finance')}</option>
                  <option value="operations">{t('departments.operations')}</option>
                  <option value="legal">{t('departments.legal')}</option>
                  <option value="other">{t('departments.other')}</option>
                </select>
                
                {formData.department === 'other' && (
                  <div className="mt-3">
                    <input
                      type="text"
                      name="custom_department"
                      value={formData.custom_department}
                      onChange={handleInputChange}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={t('candidates.addReferenceModal.fields.customDepartment')}
                      required
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('candidates.addReferenceModal.jobLevel')}*
                </label>
                <select
                  name="seniority"
                  value={formData.seniority}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="junior">{t('seniority.junior')}</option>
                  <option value="mid_level">{t('seniority.mid_level')}</option>
                  <option value="senior">{t('seniority.senior')}</option>
                  <option value="manager">{t('seniority.manager')}</option>
                  <option value="senior_manager">{t('seniority.senior_manager')}</option>
                  <option value="director">{t('seniority.director')}</option>
                  <option value="ceo">{t('seniority.ceo')}</option>
                </select>
              </div>
            </div>
          </div>

          <div className="mb-6 bg-blue-50/40 rounded-lg p-4 border border-blue-100">
            <h3 className="text-sm font-medium text-blue-600 mb-2">
              {t('candidates.addReferenceModal.relationshipInfo')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('candidates.addReferenceModal.fields.relationshipType')}*
                </label>
                <select
                  name="relationship_type"
                  value={formData.relationship_type}
                  onChange={handleInputChange}
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="leader">{t('relationships.leader')}</option>
                  <option value="peer">{t('relationships.peer')}</option>
                  <option value="subordinate">{t('relationships.subordinate')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('candidates.addReferenceModal.fields.yearsWorked')}*
                </label>
                <input
                  type="number"
                  name="years_worked"
                  value={formData.years_worked}
                  onChange={handleInputChange}
                  min="1"
                  max="20"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-8 border-t pt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1.5"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-75 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('settings.organization.saving')}
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                    <polyline points="17 21 17 13 7 13 7 21"></polyline>
                    <polyline points="7 3 7 8 15 8"></polyline>
                  </svg>
                  {t('settings.organization.save')}
                </>
              )}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default AddReferenceModal;